import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import errorMessage from '../../../helpers/errorMessage';
import PageHeader from '../../common/PageHeader';
import EmptyState from '../../common/EmptyState';
import LoadingState from '../../common/LoadingState';

// Platforms > {platform} > API Access (admin only): this platform's External
// API (../external_api) client accounts - backend/api/external_api_accounts.js.
// Create an account, switch the platform's API access on/off, and generate a
// new secret. A secret is only ever shown once, right after it is generated.

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const errMsg = (err) => errorMessage(err);
const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_');

const StatusPill = ({ enabled }) => (
    <span className={`ex-status ${enabled ? 'ex-status-success' : 'ex-status-danger'}`}>{enabled ? 'Enabled' : 'Disabled'}</span>
);

const SecretDialog = ({ secret, onHide }) => {
    const [copied, setCopied] = useState(false);
    if (!secret) return null;
    const copy = () => navigator.clipboard.writeText(secret.client_secret).then(() => setCopied(true));
    return (
        <Dialog visible header="New client secret" modal style={{ width: '520px' }} onHide={onHide}
            footer={<Button label="I have saved it" icon="pi pi-check" onClick={onHide} />}>
            <p className="ex-inline-error" style={{ marginTop: 0 }}>
                Copy this secret now - it will not be shown again. Any existing tokens for this client stop working immediately.
            </p>
            <dl className="ex-kv">
                <dt>Client ID</dt><dd><code>{secret.client_id}</code></dd>
                <dt>Client secret</dt><dd><code style={{ wordBreak: 'break-all' }}>{secret.client_secret}</code></dd>
            </dl>
            <Button label={copied ? 'Copied' : 'Copy secret'} icon={copied ? 'pi pi-check' : 'pi pi-copy'} className="p-button-outlined" onClick={copy} />
        </Dialog>
    );
};

export const PlatformApiAccess = () => {
    const { platformName } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [secret, setSecret] = useState(null);
    const [rotateTarget, setRotateTarget] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [clientId, setClientId] = useState('');

    const load = () => {
        setError(null);
        axiosWithAuth.get('/external-api/accounts', { params: { platform: platformName } })
            .then((res) => setData(res.data))
            .catch((err) => setError(errMsg(err)));
    };

    useEffect(load, [platformName]); // eslint-disable-line react-hooks/exhaustive-deps

    const run = (promise) => {
        setBusy(true);
        return promise
            .catch((err) => alert(errMsg(err)))
            .finally(() => { setBusy(false); load(); });
    };

    // Real history step back (like PlatformConfiguration's own Back), so the
    // two pages' Back buttons don't bounce between each other. Opened
    // directly (no in-app history, location.key "default"): go to the platform.
    const goBack = () => (location.key !== 'default' ? navigate(-1) : navigate(`/platforms/${platformName}`));

    const accounts = data?.accounts || [];
    const anyEnabled = accounts.some((a) => a.api_enabled);
    const defaultClientId = data ? `${slug(platformName)}_${data.env}` : '';

    const togglePlatform = (enabled) =>
        run(axiosWithAuth.patch(`/external-api/platforms/${encodeURIComponent(platformName)}`, { api_enabled: enabled }));

    const toggleAccount = (account, enabled) =>
        run(axiosWithAuth.patch(`/external-api/accounts/${account._key}`, { api_enabled: enabled }));

    const rotate = () => {
        const target = rotateTarget;
        setRotateTarget(null);
        run(axiosWithAuth.post(`/external-api/accounts/${target._key}/rotate`)
            .then((res) => setSecret({ client_id: res.data.account.client_id, client_secret: res.data.client_secret })));
    };

    const create = () => {
        setCreateOpen(false);
        run(axiosWithAuth.post('/external-api/accounts', { platform: platformName, ...(clientId ? { client_id: clientId } : {}) })
            .then((res) => setSecret({ client_id: res.data.account.client_id, client_secret: res.data.client_secret })));
    };

    return (
        <div className="content-container">
            <PageHeader
                title={`${platformName} API Access`}
                subtitle={data ? `External API client credentials (${data.env} · ${data.db_name}).` : 'External API client credentials.'}
                icon="pi-key"
                actions={<>
                    <Button label="Back" icon="pi pi-arrow-left" className="p-button-text" onClick={goBack} />
                    <Button label="Create account" icon="pi pi-plus" disabled={busy || !data}
                        onClick={() => { setClientId(''); setCreateOpen(true); }} />
                </>}
            />

            {error && <div className="ex-inline-error">{error}</div>}
            {!data && !error && <LoadingState label="Loading API accounts…" />}
            {data && accounts.length === 0 && (
                <EmptyState icon="pi-key" title="No API account yet"
                    message={`${platformName} has no External API account. Create one to give the Originator access.`}
                    action={<Button label="Create account" icon="pi pi-plus" onClick={() => { setClientId(''); setCreateOpen(true); }} />} />
            )}

            {accounts.length > 0 && (
                <section className="ex-section">
                    <div className="ex-section-header">
                        <div>
                            <div className="ex-section-title">API access <StatusPill enabled={anyEnabled} /></div>
                            <div className="ex-section-subtitle">
                                Switching off blocks every account below immediately; switching on restores their current secrets.
                            </div>
                        </div>
                        <div className="ex-toolbar-group">
                            <InputSwitch checked={anyEnabled} disabled={busy} onChange={(e) => togglePlatform(e.value)} />
                        </div>
                    </div>
                    <DataTable value={accounts} dataKey="_key" size="small" responsiveLayout="scroll">
                        <Column field="client_id" header="Client ID" body={(a) => <code>{a.client_id}</code>} />
                        <Column header="Status" body={(a) => <StatusPill enabled={a.api_enabled} />} />
                        <Column field="coll_name" header="Collection" />
                        <Column header="Last token issued" body={(a) => fmtDate(a.last_token_issued_at)} />
                        <Column header="Secret last changed" body={(a) => (a.secret_rotated_at
                            ? <span>{fmtDate(a.secret_rotated_at)}<br /><small className="text-color-secondary">{a.secret_rotated_by}</small></span>
                            : '—')} />
                        <Column header="" style={{ textAlign: 'right', whiteSpace: 'nowrap' }} body={(a) => (
                            <>
                                {accounts.length > 1 && (
                                    <InputSwitch checked={a.api_enabled} disabled={busy} tooltip={a.api_enabled ? 'Disable this account' : 'Enable this account'}
                                        onChange={(e) => toggleAccount(a, e.value)} style={{ verticalAlign: 'middle', marginRight: '0.75rem' }} />
                                )}
                                <Button label="Change secret" icon="pi pi-refresh" className="p-button-sm p-button-outlined"
                                    disabled={busy} onClick={() => setRotateTarget(a)} />
                            </>
                        )} />
                    </DataTable>
                </section>
            )}

            <Dialog visible={Boolean(rotateTarget)} header="Change client secret?" modal style={{ width: '450px' }}
                onHide={() => setRotateTarget(null)}
                footer={<>
                    <Button label="Cancel" className="p-button-text" onClick={() => setRotateTarget(null)} />
                    <Button label="Generate new secret" icon="pi pi-refresh" className="p-button-danger" onClick={rotate} />
                </>}>
                <p>A new secret will be generated for <code>{rotateTarget?.client_id}</code>.</p>
                <p>The current secret and any tokens issued with it stop working immediately - the Originator's integration must be updated with the new secret.</p>
            </Dialog>

            <Dialog visible={createOpen} header={`Create External API account for ${platformName}`} modal className="p-fluid" style={{ width: '450px' }}
                onHide={() => setCreateOpen(false)}
                footer={<>
                    <Button label="Cancel" className="p-button-text" onClick={() => setCreateOpen(false)} />
                    <Button label="Create" icon="pi pi-check" onClick={create} />
                </>}>
                <div className="field">
                    <label htmlFor="api_client_id">Client ID</label>
                    <InputText id="api_client_id" value={clientId} placeholder={defaultClientId}
                        onChange={(e) => setClientId(e.target.value)} />
                    <small className="text-color-secondary">Leave empty to use {defaultClientId}. A secret is generated and shown once.</small>
                </div>
            </Dialog>

            <SecretDialog secret={secret} onHide={() => setSecret(null)} />
        </div>
    );
};
